// src/pages/admin/DepositHistory.js
import React, { useState, useEffect } from 'react';
import { database, ref, get, onValue } from '../../firebase';
import { 
  Search, Filter, Download, Eye, Calendar, DollarSign, 
  Gift, User, Package, Wallet, Clock, CheckCircle, 
  XCircle, AlertCircle, TrendingUp, BarChart, 
  RefreshCw, CreditCard, Hash, PieChart, Activity,
  Database, MoreVertical, Mail, Phone, MapPin, Shield
} from 'lucide-react';

const DepositHistory = () => {
  const [deposits, setDeposits] = useState([]);
  const [users, setUsers] = useState({}); // Store user data
  const [filteredDeposits, setFilteredDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    package: 'all',
    paymentMethod: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [stats, setStats] = useState({
    totalDeposits: 0,
    totalAmount: 0,
    totalBonus: 0,
    approvedCount: 0,
    pendingCount: 0,
    rejectedCount: 0
  });

  useEffect(() => {
    fetchDeposits();
    fetchUsers(); // Fetch users data
    
    // Set up real-time listeners
    const depositsRef = ref(database, 'DepositHistory');
    const usersRef = ref(database, 'users');
    
    const unsubscribeDeposits = onValue(depositsRef, () => {
      fetchDeposits();
    });
    
    const unsubscribeUsers = onValue(usersRef, () => {
      fetchUsers();
    });
    
    return () => {
      unsubscribeDeposits();
      unsubscribeUsers();
    };
  }, []);

  useEffect(() => {
    filterAndSortDeposits();
    calculateStats();
  }, [deposits, users, searchTerm, filters, sortConfig]);

  // Fetch users data from Firebase
  const fetchUsers = async () => {
    try {
      console.log('Fetching users from Firebase...');
      
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      const usersMap = {};
      
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        console.log('Users found:', Object.keys(usersData).length);
        
        // Loop through all users
        Object.keys(usersData).forEach(userKey => {
          const user = usersData[userKey];
          
          if (user) {
            // Store user data with userId as key
            usersMap[userKey] = {
              userId: userKey,
              name: user.name || user.userName || 'Unknown',
              email: user.email || 'No email',
              mobile: user.mobile || user.phone || 'No phone',
              country: user.country || 'Unknown',
              status: user.status || 'inactive',
              fundBalance: parseFloat(user.fundBalance) || 0,
              aurumBalance: parseFloat(user.aurumBalance) || 0,
              depositTotalAmount: parseFloat(user.depositTotalAmount) || 0
            };
          }
        });
      }
      
      console.log('Total users loaded:', Object.keys(usersMap).length);
      setUsers(usersMap);
      
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers({});
    }
  };

  const fetchDeposits = async () => {
    try {
      setLoading(true);
      console.log('Fetching deposits from Firebase...');
      
      const depositsRef = ref(database, 'DepositHistory');
      const snapshot = await get(depositsRef);
      
      const depositsArray = [];
      
      if (snapshot.exists()) {
        const depositsData = snapshot.val();
        console.log('Deposits found:', Object.keys(depositsData).length);
        
        // Loop through all deposits
        Object.keys(depositsData).forEach(depositKey => {
          const deposit = depositsData[depositKey];
          
          if (deposit) {
            depositsArray.push({
              id: depositKey,
              depositId: deposit.depositId || depositKey,
              memberId: deposit.memberId || 'Unknown',
              amount: parseFloat(deposit.amount) || 0,
              bonus: parseFloat(deposit.bonus) || 0,
              totalAdded: parseFloat(deposit.totalAdded) || (parseFloat(deposit.amount) || 0) + (parseFloat(deposit.bonus) || 0),
              paymentMethod: deposit.paymentMethod || 'Unknown',
              paymentMethodName: deposit.paymentMethodName || deposit.paymentMethod,
              packageId: deposit.packageId || '',
              packageName: deposit.packageName || getPackageName(deposit.amount),
              status: deposit.status || 'pending',
              transactionPassword: deposit.transactionPassword || '',
              createdAt: deposit.createdAt || '',
              approvedAt: deposit.approvedAt || '',
              newAddedFund: parseFloat(deposit.newAddedFund) || 0,
              newDepositTotal: parseFloat(deposit.newDepositTotal) || parseFloat(deposit.amount) || 0,
              newFundBalance: parseFloat(deposit.newFundBalance) || 0,
              previousAddedFund: parseFloat(deposit.previousAddedFund) || 0,
              previousDepositTotal: parseFloat(deposit.previousDepositTotal) || 0,
              previousFundBalance: parseFloat(deposit.previousFundBalance) || 0,
              type: deposit.type || 'deposit'
            });
          }
        });
      }
      
      // Sort by date (newest first)
      depositsArray.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA;
      });
      
      console.log('Total deposits loaded:', depositsArray.length);
      setDeposits(depositsArray);
      
    } catch (error) {
      console.error('Error fetching deposits:', error);
      setDeposits([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const statsData = {
      totalDeposits: filteredDeposits.length,
      totalAmount: 0,
      totalBonus: 0,
      approvedCount: 0,
      pendingCount: 0,
      rejectedCount: 0
    };
    
    filteredDeposits.forEach(deposit => {
      statsData.totalAmount += deposit.amount || 0;
      statsData.totalBonus += deposit.bonus || 0;
      
      switch(deposit.status?.toLowerCase()) {
        case 'approved':
          statsData.approvedCount++;
          break;
        case 'pending':
          statsData.pendingCount++;
          break;
        case 'rejected':
          statsData.rejectedCount++;
          break;
        default:
          statsData.pendingCount++;
      }
    });
    
    setStats(statsData);
  };

  const filterAndSortDeposits = () => {
    let filtered = [...deposits];
    
    // Apply search - now also search by user name
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d => {
        const memberId = d.memberId || '';
        const userName = getUserName(d.memberId) || '';
        return (
          memberId.toLowerCase().includes(term) ||
          userName.toLowerCase().includes(term) ||
          (d.depositId && d.depositId.toLowerCase().includes(term)) ||
          (d.paymentMethod && d.paymentMethod.toLowerCase().includes(term))
        );
      });
    }
    
    // Apply filters
    if (filters.status !== 'all') {
      filtered = filtered.filter(d => d.status?.toLowerCase() === filters.status);
    }
    
    if (filters.package !== 'all') {
      filtered = filtered.filter(d => {
        const packageName = d.packageName?.toLowerCase() || '';
        return packageName.includes(filters.package);
      });
    }
    
    if (filters.paymentMethod !== 'all') {
      filtered = filtered.filter(d => 
        d.paymentMethod?.toLowerCase() === filters.paymentMethod
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      
      if (sortConfig.key === 'amount' || sortConfig.key === 'bonus' || sortConfig.key === 'totalAdded') {
        const numA = aValue;
        const numB = bValue;
        return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
      }
      
      if (sortConfig.key === 'createdAt') {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredDeposits(filtered);
  };

  // Helper function to get user name from memberId
  const getUserName = (memberId) => {
    if (!memberId || !users[memberId]) {
      return 'Unknown User';
    }
    return users[memberId].name || 'Unknown User';
  };

  // Helper function to get user info
  const getUserInfo = (memberId) => {
    if (!memberId || !users[memberId]) {
      return {
        name: 'Unknown User',
        email: 'No email',
        mobile: 'No phone',
        country: 'Unknown',
        status: 'inactive',
        fundBalance: 0,
        aurumBalance: 0,
        depositTotalAmount: 0
      };
    }
    return users[memberId];
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-900/30 text-green-400 border border-green-600/30';
      case 'pending':
        return 'bg-yellow-900/30 text-yellow-400 border border-yellow-600/30';
      case 'rejected':
        return 'bg-red-900/30 text-red-400 border border-red-600/30';
      default:
        return 'bg-gold-900/20 text-gold-300 border border-gold-600/20';
    }
  };

  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getPackageName = (amount) => {
    const amt = parseFloat(amount) || 0;
    if (amt >= 50000) return 'Package 5';
    if (amt >= 10000) return 'Package 4';
    if (amt >= 2500) return 'Package 3';
    if (amt >= 250) return 'Package 2';
    if (amt >= 10) return 'Package 1';
    return 'Custom';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const exportToCSV = () => {
    const headers = [
      'Deposit ID', 'Date', 'Member ID', 'Member Name', 'Amount', 'Bonus', 'Total Added',
      'Package', 'Payment Method', 'Status', 'Created At', 'Approved At',
      'Previous Fund Balance', 'New Fund Balance', 'Previous Deposit Total',
      'New Deposit Total'
    ];
    
    const csvData = filteredDeposits.map(deposit => {
      const userInfo = getUserInfo(deposit.memberId);
      return [
        deposit.depositId,
        formatDate(deposit.createdAt),
        deposit.memberId,
        userInfo.name,
        deposit.amount,
        deposit.bonus,
        deposit.totalAdded,
        deposit.packageName,
        deposit.paymentMethod,
        deposit.status,
        deposit.createdAt,
        deposit.approvedAt || '',
        deposit.previousFundBalance || 0,
        deposit.newFundBalance || 0,
        deposit.previousDepositTotal || 0,
        deposit.newDepositTotal || deposit.amount
      ];
    });

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deposit-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      status: 'all',
      package: 'all',
      paymentMethod: 'all'
    });
  };

  const getPaymentMethodIcon = (method) => {
    if (!method) return <CreditCard className="h-4 w-4" />;
    
    const methodLower = method.toLowerCase();
    if (methodLower.includes('bitcoin')) return <Hash className="h-4 w-4 text-orange-500" />;
    if (methodLower.includes('usdt')) return <DollarSign className="h-4 w-4 text-green-500" />;
    return <CreditCard className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gold-900/10 via-black to-gold-900/10 pt-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold-500"></div>
            <span className="ml-4 text-gold-300">Loading deposit history...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gold-900/10 via-black to-gold-900/10 pt-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gold-100 mb-2">Deposit History</h1>
              <p className="text-gold-400">View all deposit transactions</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-gold-400 bg-gold-900/30 px-3 py-1 rounded-full flex items-center gap-1 border border-gold-600/30">
                <Database className="h-3 w-3" />
                {filteredDeposits.length} Records
              </div>
              <button
                onClick={() => {
                  fetchDeposits();
                  fetchUsers();
                }}
                className="p-2 bg-gold-500 hover:bg-gold-400 text-black rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gold-900/30 border border-gold-600/50 rounded-lg p-4">
            <div className="text-gold-400 text-sm mb-1">Total Deposits</div>
            <div className="text-xl font-bold text-gold-100">{formatNumber(stats.totalDeposits)}</div>
            <div className="text-xs text-gold-300 mt-1">${stats.totalAmount.toFixed(2)}</div>
          </div>
          
          <div className="bg-gold-900/30 border border-gold-600/50 rounded-lg p-4">
            <div className="text-gold-400 text-sm mb-1">Total Bonus</div>
            <div className="text-xl font-bold text-green-400">{formatCurrency(stats.totalBonus)}</div>
            <div className="text-xs text-gold-300 mt-1">Approved: {stats.approvedCount}</div>
          </div>
          
          <div className="bg-gold-900/30 border border-gold-600/50 rounded-lg p-4">
            <div className="text-gold-400 text-sm mb-1">Pending</div>
            <div className="text-xl font-bold text-yellow-400">{stats.pendingCount}</div>
            <div className="text-xs text-gold-300 mt-1">Awaiting approval</div>
          </div>
          
          <div className="bg-gold-900/30 border border-gold-600/50 rounded-lg p-4">
            <div className="text-gold-400 text-sm mb-1">Average</div>
            <div className="text-xl font-bold text-gold-200">
              {formatCurrency(stats.totalDeposits > 0 ? stats.totalAmount / stats.totalDeposits : 0)}
            </div>
            <div className="text-xs text-gold-300 mt-1">Per deposit</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gold-900/30 border border-gold-600/50 rounded-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <h3 className="text-lg font-semibold text-gold-100 flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </h3>
            <div className="flex gap-2">
              <button
                onClick={resetFilters}
                className="px-3 py-2 bg-gold-800/50 hover:bg-gold-700/50 text-gold-100 rounded-lg flex items-center gap-2 text-sm border border-gold-600/30 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Reset
              </button>
              <button
                onClick={exportToCSV}
                className="px-3 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center gap-2 text-sm transition-colors"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or ID..."
                className="w-full pl-9 pr-3 py-2 bg-gold-900/50 border border-gold-600/50 rounded-lg text-gold-100 placeholder-gold-500 text-sm focus:outline-none focus:border-gold-500"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gold-500" />
            </div>
            
            {/* Status */}
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="px-3 py-2 bg-gold-900/50 border border-gold-600/50 rounded-lg text-gold-100 text-sm focus:outline-none focus:border-gold-500"
            >
              <option value="all">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
            
            {/* Package */}
            <select
              value={filters.package}
              onChange={(e) => setFilters({...filters, package: e.target.value})}
              className="px-3 py-2 bg-gold-900/50 border border-gold-600/50 rounded-lg text-gold-100 text-sm focus:outline-none focus:border-gold-500"
            >
              <option value="all">All Packages</option>
              <option value="basic">Basic Plan</option>
              <option value="package 1">Package 1</option>
              <option value="package 2">Package 2</option>
            </select>
            
            {/* Payment Method */}
            <select
              value={filters.paymentMethod}
              onChange={(e) => setFilters({...filters, paymentMethod: e.target.value})}
              className="px-3 py-2 bg-gold-900/50 border border-gold-600/50 rounded-lg text-gold-100 text-sm focus:outline-none focus:border-gold-500"
            >
              <option value="all">All Payment Methods</option>
              <option value="bitcoin">Bitcoin</option>
              <option value="usdt">USDT</option>
              <option value="bank">Bank Transfer</option>
            </select>
          </div>
        </div>

        {/* Table Container */}
        <div className="bg-gold-900/30 border border-gold-600/50 rounded-lg overflow-hidden mb-6">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gold-800/30">
                <tr>
                  <th className="py-3 px-2 text-left text-gold-400 font-semibold text-xs border-b border-gold-600/30">
                    <button 
                      onClick={() => handleSort('createdAt')}
                      className="flex items-center gap-1 hover:text-gold-200 transition-colors"
                    >
                      Date
                      {sortConfig.key === 'createdAt' && (
                        sortConfig.direction === 'asc' ? '↑' : '↓'
                      )}
                    </button>
                  </th>
                  <th className="py-3 px-2 text-left text-gold-400 font-semibold text-xs border-b border-gold-600/30">
                    Deposit ID
                  </th>
                  <th className="py-3 px-2 text-left text-gold-400 font-semibold text-xs border-b border-gold-600/30">
                    Member
                  </th>
                  <th className="py-3 px-2 text-left text-gold-400 font-semibold text-xs border-b border-gold-600/30">
                    <button 
                      onClick={() => handleSort('amount')}
                      className="flex items-center gap-1 hover:text-gold-200 transition-colors"
                    >
                      Amount
                      {sortConfig.key === 'amount' && (
                        sortConfig.direction === 'asc' ? '↑' : '↓'
                      )}
                    </button>
                  </th>
                  <th className="py-3 px-2 text-left text-gold-400 font-semibold text-xs border-b border-gold-600/30">
                    Package
                  </th>
                  <th className="py-3 px-2 text-left text-gold-400 font-semibold text-xs border-b border-gold-600/30">
                    Payment
                  </th>
                  <th className="py-3 px-2 text-left text-gold-400 font-semibold text-xs border-b border-gold-600/30">
                    Status
                  </th>
                  <th className="py-3 px-2 text-left text-gold-400 font-semibold text-xs border-b border-gold-600/30">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-600/20">
                {filteredDeposits.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center">
                      <div className="text-gold-500 text-3xl mb-3">📭</div>
                      <h3 className="text-lg font-semibold text-gold-200 mb-2">No Deposits Found</h3>
                      <p className="text-gold-400 text-sm">
                        {searchTerm || Object.values(filters).some(v => v !== 'all') 
                          ? 'No deposits match your filters.'
                          : 'No deposit history found.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredDeposits.map((deposit, index) => {
                    const userInfo = getUserInfo(deposit.memberId);
                    
                    return (
                      <tr key={deposit.id} className={`hover:bg-gold-900/10 transition-colors ${index % 2 === 0 ? 'bg-gold-900/10' : ''}`}>
                        {/* Date */}
                        <td className="py-3 px-2">
                          <div className="text-gold-100 text-xs">{formatDate(deposit.createdAt)}</div>
                          <div className="text-gold-500 text-[10px]">
                            {deposit.createdAt ? new Date(deposit.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                          </div>
                        </td>
                        
                        {/* Deposit ID */}
                        <td className="py-3 px-2">
                          <div className="text-gold-200 text-xs font-mono truncate max-w-[90px]">
                            {deposit.depositId}
                          </div>
                        </td>
                        
                        {/* Member - Now shows Name and ID */}
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-1">
                            <div className="w-5 h-5 rounded-full bg-gold-500/20 flex items-center justify-center flex-shrink-0 border border-gold-500/30">
                              <User className="h-2.5 w-2.5 text-gold-400" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-gold-100 text-xs font-medium truncate max-w-[100px]">
                                {userInfo.name}
                              </div>
                              <div className="text-gold-500 text-[10px] truncate max-w-[100px]">
                                ID: {deposit.memberId}
                              </div>
                              {userInfo.status && (
                                <div className={`inline-block px-1 py-0.5 rounded text-[8px] border ${
                                  userInfo.status === 'active' 
                                    ? 'bg-green-900/30 text-green-400 border-green-600/30' 
                                    : 'bg-red-900/30 text-red-400 border-red-600/30'
                                }`}>
                                  {userInfo.status}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        
                        {/* Amount */}
                        <td className="py-3 px-2">
                          <div className="text-gold-100 font-medium text-xs">
                            {formatCurrency(deposit.amount)}
                          </div>
                          {deposit.bonus > 0 && (
                            <div className="text-green-400 text-[10px] flex items-center gap-0.5">
                              <Gift className="h-2.5 w-2.5" />
                              +{formatCurrency(deposit.bonus)}
                            </div>
                          )}
                        </td>
                        
                        {/* Package */}
                        <td className="py-3 px-2">
                          <div className="text-gold-100 text-xs truncate max-w-[80px]">
                            {deposit.packageName}
                          </div>
                        </td>
                        
                        {/* Payment */}
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-1">
                            {getPaymentMethodIcon(deposit.paymentMethod)}
                            <div className="text-gold-100 text-xs truncate max-w-[70px]">
                              {deposit.paymentMethod}
                            </div>
                          </div>
                        </td>
                        
                        {/* Status */}
                        <td className="py-3 px-2">
                          <div className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] ${getStatusColor(deposit.status)}`}>
                            {getStatusIcon(deposit.status)}
                            <span>{deposit.status}</span>
                          </div>
                        </td>
                        
                        {/* Actions */}
                        <td className="py-3 px-2">
                          <button
                            onClick={() => setSelectedDeposit(deposit)}
                            className="p-1 hover:bg-gold-800/30 rounded transition-colors border border-transparent hover:border-gold-600/30"
                            title="View Details"
                          >
                            <MoreVertical className="h-3.5 w-3.5 text-gold-400 hover:text-gold-200" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Show Total Count */}
        <div className="text-sm text-gold-400 mb-8">
          Showing {filteredDeposits.length} of {deposits.length} total deposits
        </div>
      </div>

      {/* Modal */}
      {selectedDeposit && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gold-900/90 border border-gold-600/50 rounded-lg p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto backdrop-blur-sm">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-gold-100">Deposit Details</h3>
                <p className="text-gold-400 text-sm mt-1">{selectedDeposit.depositId}</p>
              </div>
              <button 
                onClick={() => setSelectedDeposit(null)} 
                className="text-gold-400 hover:text-gold-200 p-1 hover:bg-gold-800/30 rounded-full w-8 h-8 flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            
            {/* Member Info - Now with fetched user details */}
            <div className="mb-6">
              <h4 className="text-lg font-bold text-gold-100 mb-3 flex items-center gap-2">
                <User className="h-5 w-5 text-gold-400" />
                Member Information
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                  <div className="text-gold-400 text-xs mb-1">Member Name</div>
                  <div className="text-gold-100 font-medium">{getUserName(selectedDeposit.memberId)}</div>
                </div>
                <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                  <div className="text-gold-400 text-xs mb-1">Member ID</div>
                  <div className="text-gold-100 font-medium">{selectedDeposit.memberId}</div>
                </div>
              </div>
              
              {/* Additional user info */}
              {(() => {
                const userInfo = getUserInfo(selectedDeposit.memberId);
                return (
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-gold-900/30 p-2 rounded border border-gold-600/20">
                      <div className="text-gold-400 text-[10px] mb-0.5">Email</div>
                      <div className="text-gold-100 text-xs truncate">{userInfo.email}</div>
                    </div>
                    <div className="bg-gold-900/30 p-2 rounded border border-gold-600/20">
                      <div className="text-gold-400 text-[10px] mb-0.5">Mobile</div>
                      <div className="text-gold-100 text-xs">{userInfo.mobile}</div>
                    </div>
                    <div className="bg-gold-900/30 p-2 rounded border border-gold-600/20">
                      <div className="text-gold-400 text-[10px] mb-0.5">Status</div>
                      <div className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] border ${
                        userInfo.status === 'active' 
                          ? 'bg-green-900/30 text-green-400 border-green-600/30' 
                          : 'bg-red-900/30 text-red-400 border-red-600/30'
                      }`}>
                        {userInfo.status}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            
            {/* Deposit Details */}
            <div className="mb-6">
              <h4 className="text-lg font-bold text-gold-100 mb-3 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-gold-400" />
                Deposit Information
              </h4>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                    <div className="text-gold-400 text-xs mb-1">Amount</div>
                    <div className="text-gold-100 text-xl font-bold">{formatCurrency(selectedDeposit.amount)}</div>
                  </div>
                  <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                    <div className="text-gold-400 text-xs mb-1">Bonus</div>
                    <div className="text-green-400 text-xl font-bold">{formatCurrency(selectedDeposit.bonus)}</div>
                  </div>
                </div>
                
                <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                  <div className="text-gold-400 text-xs mb-1">Total Added</div>
                  <div className="text-gold-100 text-2xl font-bold">{formatCurrency(selectedDeposit.totalAdded)}</div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                    <div className="text-gold-400 text-xs mb-1">Package</div>
                    <div className="text-gold-100 font-medium">{selectedDeposit.packageName}</div>
                  </div>
                  <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                    <div className="text-gold-400 text-xs mb-1">Payment Method</div>
                    <div className="text-gold-100 font-medium flex items-center gap-2">
                      {getPaymentMethodIcon(selectedDeposit.paymentMethod)}
                      {selectedDeposit.paymentMethod}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Balance Changes */}
            <div className="mb-6">
              <h4 className="text-lg font-bold text-gold-100 mb-3">Balance Changes</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                  <div className="text-gold-400 text-xs mb-1">Previous Deposit Total</div>
                  <div className="text-gold-100 font-medium">{formatCurrency(selectedDeposit.previousDepositTotal)}</div>
                </div>
                <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                  <div className="text-gold-400 text-xs mb-1">New Deposit Total</div>
                  <div className="text-green-400 font-bold">{formatCurrency(selectedDeposit.newDepositTotal)}</div>
                </div>
              </div>
            </div>
            
            {/* Dates */}
            <div className="mb-6">
              <h4 className="text-lg font-bold text-gold-100 mb-3">Timestamps</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                  <div className="text-gold-400 text-xs mb-1">Created At</div>
                  <div className="text-gold-100 text-sm">{formatDateTime(selectedDeposit.createdAt)}</div>
                </div>
                {selectedDeposit.approvedAt && (
                  <div className="bg-gold-900/50 p-3 rounded border border-gold-600/30">
                    <div className="text-gold-400 text-xs mb-1">Approved At</div>
                    <div className="text-gold-100 text-sm">{formatDateTime(selectedDeposit.approvedAt)}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end pt-4 border-t border-gold-600/30">
              <button
                onClick={() => setSelectedDeposit(null)}
                className="px-4 py-2 bg-gold-800/50 hover:bg-gold-700/50 text-gold-100 rounded-lg text-sm border border-gold-600/30 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositHistory;